import React, { useState, useCallback } from 'react';
import ScrollReveal from './ScrollReveal';
import { trackContactFormSubmit, trackEmailClick } from '../utils/analytics';

const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSePSBjMYdC_NIO6EUxuuDATEm79_LC5BtA3vLc9Zu0BH-OP3g/formResponse';

const ENTRY_IDS = {
  fullName: 'entry.910632519',
  organization: 'entry.1464588105',
  email: 'entry.1185486508',
  facilityType: 'entry.1326614296',
  numSites: 'entry.1463533365',
  interest: 'entry.2006292378',
  message: 'entry.511643246',
};

function submitToGoogleForm(data) {
  const params = new URLSearchParams();
  Object.entries(ENTRY_IDS).forEach(([key, entryId]) => {
    if (data[key]) params.append(entryId, data[key]);
  });

  // Google Forms doesn't support CORS, so we submit via a hidden iframe
  // to avoid CORS errors while still delivering the data
  const iframe = document.createElement('iframe');
  iframe.name = 'google-form-iframe';
  iframe.style.display = 'none';
  document.body.appendChild(iframe);

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = GOOGLE_FORM_URL;
  form.target = 'google-form-iframe';

  params.forEach((value, key) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();

  // Clean up after submission
  setTimeout(() => {
    document.body.removeChild(form);
    document.body.removeChild(iframe);
  }, 2000);
}

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setError(false);

    const formEl = e.target;
    const data = {
      fullName: formEl.fullName.value,
      organization: formEl.organization.value,
      email: formEl.email.value,
      facilityType: formEl.facilityType.value,
      numSites: formEl.numSites.value,
      interest: formEl.interest.value,
      message: formEl.message.value,
    };

    try {
      submitToGoogleForm(data);
      trackContactFormSubmit({
        facilityType: data.facilityType,
        numSites: data.numSites,
        interest: data.interest,
      });
      setSubmitted(true);
      formEl.reset();
      setTimeout(() => setSubmitted(false), 5000);
    } catch {
      setError(true);
    }
  }, []);

  return (
    <section className="section" id="contact">
      <div className="contact-inner">
        <ScrollReveal>
          <div className="contact-info">
            <div className="section-label">Get In Touch</div>
            <h2>Ready to see<br /><strong>structured intelligence</strong><br />from your cameras?</h2>
            <p>
              Tell us about your facility and operational goals. We'll design a deployment plan
              tailored to your site &mdash; from single-facility pilots to multi-site rollouts.
            </p>
            <div className="contact-details">
              <div className="contact-detail">
                <div className="contact-detail-icon">&#9993;</div>
                <div>
                  <div className="contact-detail-label">Email</div>
                  <div className="contact-detail-text"><a href="mailto:contact@oscilabs.com" style={{ color: 'inherit', textDecoration: 'none' }} onClick={trackEmailClick}>contact@oscilabs.com</a></div>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-detail-icon">&#9678;</div>
                <div>
                  <div className="contact-detail-label">Deployment</div>
                  <div className="contact-detail-text">Global &mdash; remote activation supported</div>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-detail-icon">&#10017;</div>
                <div>
                  <div className="contact-detail-label">Response Time</div>
                  <div className="contact-detail-text">Typically within 24 hours</div>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="contact-form-wrap">
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input type="text" name="fullName" className="form-input" placeholder="Your name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Organization</label>
                  <input type="text" name="organization" className="form-input" placeholder="Organization name" required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input type="email" name="email" className="form-input" placeholder="you@organization.com" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Facility Type</label>
                  <select name="facilityType" className="form-select" required defaultValue="">
                    <option value="" disabled>Select type</option>
                    <option>Hospital / Clinic</option>
                    <option>School / Education</option>

                    <option>Distribution Center</option>
                    <option>Government Facility</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Number of Sites</label>
                  <select name="numSites" className="form-select" required defaultValue="">
                    <option value="" disabled>Select range</option>
                    <option>1 site (pilot)</option>
                    <option>2–5 sites</option>
                    <option>6–20 sites</option>
                    <option>20+ sites</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Interest</label>
                <select name="interest" className="form-select" defaultValue="">
                  <option value="" disabled>What are you looking for?</option>
                  <option>Vision Monitoring (Core)</option>
                  <option>Vision Insights (Advanced)</option>
                  <option>Multi-Site Rollout</option>
                  <option>Custom Dashboard</option>
                  <option>General Inquiry</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  name="message"
                  className="form-textarea"
                  placeholder="Tell us about your operational goals, governance requirements, or any questions..."
                />
              </div>
              {error && (
                <p style={{ color: 'var(--red, #ff4444)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  Something went wrong. Please email us directly at contact@oscilabs.com
                </p>
              )}
              <button
                type="submit"
                className="form-submit"
                disabled={submitted}
                style={submitted ? { background: 'var(--green)', boxShadow: '0 0 20px var(--green-dim)' } : undefined}
              >
                {submitted ? "Sent \u2014 We'll be in touch" : 'Request a Demo'}
              </button>
            </form>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
